import type { Nullable, Optional } from '@vy/utils';
import { isArray, isEmptyArray, isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGlobalSearchParams } from '../contexts/useSearchParamsContext';
import { useEffectOnce } from './use-effect-once';

export const useStateParams = <Item>(
  initialState: Item,
  paramName: string,
  serialize: (selectedItems: Item) => string | undefined,
  deserialize: (state: string) => Nullable<Item>,
): [Nullable<Item>, (state: Optional<Item>) => void] => {
  const [searchParams, setSearchParams] = useGlobalSearchParams();
  const existingValue = searchParams.get(paramName);

  const decodedValue = isNotNullOrUndefined(existingValue) ? deserialize(existingValue) : null;

  const [state, setState] = useState<Nullable<Item>>(
    isNullOrUndefined(decodedValue) ? initialState : decodedValue,
  );

  const latestRef = useRef({ searchParams, setSearchParams, serialize });
  latestRef.current = { searchParams, setSearchParams, serialize };

  useEffect(() => {
    if (isNullOrUndefined(existingValue)) {
      setState(null);

      return;
    }

    const deserializedValue = deserialize(existingValue);

    if (deserializedValue !== state) {
      setState(deserializedValue);
    }
  }, [existingValue]);

  useEffectOnce(() => onChange(state));

  const onChange = useCallback((newItem: Optional<Item>) => {
    const {
      searchParams: latestSearchParams,
      setSearchParams: latestSetSearchParams,
      serialize: latestSerialize,
    } = latestRef.current;

    updateQueryParam<Item>(newItem, latestSerialize, latestSearchParams, paramName);

    setState(newItem ?? null);

    latestSetSearchParams(new URLSearchParams(latestSearchParams));
  }, [paramName]);

  return [state, onChange];
};

function updateQueryParam<Item>(
  newItem: Optional<Item>,
  serialize: (selectedItems: Item) => string | undefined,
  searchParams: URLSearchParams,
  paramName: string,
) {
  const isItemUndefined = isNullOrUndefinedOrEmptyArray(newItem);
  const newValue = isItemUndefined ? undefined : serialize(newItem);

  return isItemUndefined || isEmptyString(newValue)
    ? searchParams.delete(paramName)
    : searchParams.set(paramName, newValue);
}

function isNullOrUndefinedOrEmptyArray<Item>(item: Optional<Item> | Item[]): item is null | undefined | [] {
  return isNullOrUndefined(item) || (isArray(item) && isEmptyArray(item));
}
